import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth, getPostAuthDestination, normalizeAuthRedirect } from "@/context/AuthContext";

export default function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const hashParams = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const error = hashParams.get("error");
    const errorDescription = hashParams.get("error_description");

    if (error) {
      navigate(`/login?error=${encodeURIComponent(errorDescription || t("auth.oauth_failed"))}`, { replace: true });
      return;
    }

    if (!accessToken) {
      navigate(`/login?error=${encodeURIComponent(t("auth.oauth_failed"))}`, { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/oauth-callback", {
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (data.token) {
          localStorage.setItem("cb_token", data.token);
        }
        if (data.user) {
          localStorage.setItem("cb_user", JSON.stringify(data.user));
          setUser(data.user);
        }

        window.history.replaceState(null, "", window.location.pathname);

        const redirect = normalizeAuthRedirect(searchParams.get("redirect_to") || searchParams.get("redirect"));
        navigate(getPostAuthDestination(data.user, redirect), { replace: true });
      } catch {
        navigate("/login?error=oauth", { replace: true });
      }
    })();
  }, [navigate, searchParams, setUser, t]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="rounded-sm border border-border bg-card/60 px-5 py-4 text-center">
        <div className="label-eyebrow text-volt animate-pulse">{t("auth.authenticating")}</div>
        <p className="mt-2 text-sm text-muted-foreground">{t("auth.authenticating_hint")}</p>
      </div>
    </div>
  );
}
